import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";

const SERIES = {
  prime: { name: "MODORS PRIME", dims: "140 \u00D7 63 MM", section: "5.5\u2033 \u00D7 2.5\u2033" },
  eco: { name: "MODORS ECO", dims: "140 \u00D7 50 MM", section: "5.5\u2033 \u00D7 2\u2033" },
};

const GEOM = {
  prime: { bottom: 197, shelfY: 117, heightLabel: "63 mm (2.5\u2033)", shelfLabel: "50 mm (2\u2033)", gauge: "1.5 MM STEEL GAUGE" },
  eco: { bottom: 176, shelfY: 115, heightLabel: "50 mm (2\u2033)", shelfLabel: "38 mm (1.5\u2033)", gauge: "1.5 MM STEEL GAUGE" },
};

const SPECS = [
  { label: "Series", value: "PRIME \u00B7 ECO", sub: "PRIME 5.5\u2033\u00D72.5\u2033 (140\u00D763 mm) \u00B7 ECO 5.5\u2033\u00D72\u2033 (140\u00D750 mm)" },
  { label: "Material", value: "GPSP Steel" },
  { label: "Thickness", value: "1.5 mm" },
  { label: "Galvanization", value: "275 GSM" },
  { label: "Rebate Options", value: "Single / Double" },
];

const Profile = ({ variant, series }) => {
  const isSingle = variant === "single";
  const g = GEOM[series];
  const { bottom, shelfY } = g;
  const path = isSingle
    ? `M 77 ${bottom} H 58 V 96 H 221 V ${shelfY} H 282 V ${bottom} H 263`
    : `M 77 ${bottom} H 58 V ${shelfY} H 119 V 96 H 221 V ${shelfY} H 282 V ${bottom} H 263`;
  const label = isSingle ? "Single Rebate" : "Double Rebate";
  return (
    <svg
      viewBox="0 0 340 300"
      className="w-full h-auto"
      role="img"
      aria-label={`MODORS ${SERIES[series].name} ${label} steel door frame section profile`}
    >
      <defs>
        <pattern id={`grid-${variant}-${series}`} width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(17,17,17,0.06)" strokeWidth="1" />
        </pattern>
        <marker id={`arrow-${variant}-${series}`} markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto">
          <path d="M0,0 L7,3.5 L0,7 Z" fill="#D71920" />
        </marker>
      </defs>
      <rect width="340" height="300" fill={`url(#grid-${variant}-${series})`} />

      {/* Section profile (open channel) */}
      <path d={path} fill="none" stroke="#111111" strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round" />
      <text x="152" y={(shelfY + bottom) / 2 + 18} textAnchor="middle" fill="#8A8F93" fontFamily="Inter, sans-serif" fontSize="10" letterSpacing="2">
        {g.gauge}
      </text>

      {/* Overall width: 140 mm (5.5") */}
      <line x1="58" y1={bottom + 38} x2="282" y2={bottom + 38} stroke="#D71920" strokeWidth="1.5" markerStart={`url(#arrow-${variant}-${series})`} markerEnd={`url(#arrow-${variant}-${series})`} />
      <line x1="58" y1={bottom + 4} x2="58" y2={bottom + 35} stroke="#D71920" strokeWidth="1" opacity="0.5" />
      <line x1="282" y1={bottom + 4} x2="282" y2={bottom + 35} stroke="#D71920" strokeWidth="1" opacity="0.5" />
      <text x="170" y={bottom + 60} textAnchor="middle" fill="#D71920" fontFamily="Oswald, sans-serif" fontSize="14" fontWeight="600">
        140 mm (5.5&#8243;)
      </text>

      {/* Overall height: 63 mm PRIME / 50 mm ECO */}
      <line x1="32" y1="96" x2="32" y2={bottom} stroke="#D71920" strokeWidth="1.5" markerStart={`url(#arrow-${variant}-${series})`} markerEnd={`url(#arrow-${variant}-${series})`} />
      <line x1="34" y1="96" x2="56" y2="96" stroke="#D71920" strokeWidth="1" opacity="0.5" />
      <line x1="34" y1={bottom} x2="56" y2={bottom} stroke="#D71920" strokeWidth="1" opacity="0.5" />
      <text x="16" y={(96 + bottom) / 2} transform={`rotate(-90 16 ${(96 + bottom) / 2})`} textAnchor="middle" fill="#D71920" fontFamily="Oswald, sans-serif" fontSize="14" fontWeight="600">
        {g.heightLabel}
      </text>

      {/* Top width: 102 mm (4") single / 64 mm (2.5") double hat */}
      {isSingle ? (
        <>
          <line x1="58" y1="72" x2="221" y2="72" stroke="#D71920" strokeWidth="1.5" markerStart={`url(#arrow-${variant}-${series})`} markerEnd={`url(#arrow-${variant}-${series})`} />
          <line x1="58" y1="76" x2="58" y2="94" stroke="#D71920" strokeWidth="1" opacity="0.5" />
          <line x1="221" y1="76" x2="221" y2="94" stroke="#D71920" strokeWidth="1" opacity="0.5" />
          <text x="140" y="62" textAnchor="middle" fill="#D71920" fontFamily="Oswald, sans-serif" fontSize="13" fontWeight="600">102 mm (4&#8243;)</text>
        </>
      ) : (
        <>
          <line x1="119" y1="72" x2="221" y2="72" stroke="#D71920" strokeWidth="1.5" markerStart={`url(#arrow-${variant}-${series})`} markerEnd={`url(#arrow-${variant}-${series})`} />
          <line x1="119" y1="76" x2="119" y2="94" stroke="#D71920" strokeWidth="1" opacity="0.5" />
          <line x1="221" y1="76" x2="221" y2="94" stroke="#D71920" strokeWidth="1" opacity="0.5" />
          <text x="170" y="62" textAnchor="middle" fill="#D71920" fontFamily="Oswald, sans-serif" fontSize="13" fontWeight="600">64 mm (2.5&#8243;)</text>
        </>
      )}
      {/* Shelf height: 50 mm PRIME / 38 mm ECO */}
      <line x1="310" y1={shelfY} x2="310" y2={bottom} stroke="#D71920" strokeWidth="1.5" markerStart={`url(#arrow-${variant}-${series})`} markerEnd={`url(#arrow-${variant}-${series})`} />
      <line x1="284" y1={shelfY} x2="308" y2={shelfY} stroke="#D71920" strokeWidth="1" opacity="0.5" />
      <line x1="284" y1={bottom} x2="308" y2={bottom} stroke="#D71920" strokeWidth="1" opacity="0.5" />
      <text x="325" y={(shelfY + bottom) / 2} transform={`rotate(-90 325 ${(shelfY + bottom) / 2})`} textAnchor="middle" fill="#D71920" fontFamily="Oswald, sans-serif" fontSize="13" fontWeight="600">
        {g.shelfLabel}
      </text>
      {/* Feet: 12 mm */}
      <text x="75" y={bottom - 7} textAnchor="middle" fill="#8A8F93" fontFamily="Inter, sans-serif" fontSize="9">12 mm</text>
      <text x="265" y={bottom - 7} textAnchor="middle" fill="#8A8F93" fontFamily="Inter, sans-serif" fontSize="9">12 mm</text>

      {/* Title */}
      <text x="170" y="288" textAnchor="middle" fill="#111111" fontFamily="Oswald, sans-serif" fontSize="15" fontWeight="600" letterSpacing="2">
        {label.toUpperCase()}
      </text>
    </svg>
  );
};

export const SectionDiagram = () => {
  const [series, setSeries] = useState("prime");
  return (
    <section id="section" data-testid="section-diagram" className="bg-white py-24 sm:py-32 border-t border-black/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="bg-white border border-black/15 p-5 sm:p-8 relative">
            <span className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-brand-red" />
            <span className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-brand-red" />
            <div className="flex flex-wrap gap-2 mb-6">
              {Object.entries(SERIES).map(([key, s]) => (
                <button
                  key={key}
                  data-testid={`series-${key}-btn`}
                  onClick={() => setSeries(key)}
                  className={`px-4 py-2.5 text-[11px] font-semibold tracking-[0.15em] uppercase border transition-colors duration-300 ${
                    series === key
                      ? "bg-brand-ink text-white border-brand-ink"
                      : "bg-white text-brand-ink/60 border-black/15 hover:border-brand-red hover:text-brand-red"
                  }`}
                >
                  {s.name} · {s.section}
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={series}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-2 gap-3 sm:gap-6"
              >
                <Profile variant="single" series={series} />
                <Profile variant="double" series={series} />
              </motion.div>
            </AnimatePresence>
            <div className="mt-5 pt-4 border-t border-black/10 flex flex-wrap items-center justify-between gap-4">
              <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-brand-steel">
                MODORS Engineering Drawing · {SERIES[series].name} · {SERIES[series].dims}
              </span>
              <a
                href={`/drawings/modors-${series}-section.pdf`}
                download={`MODORS-${SERIES[series].name.replace(" ", "-")}-Section-Drawing.pdf`}
                data-testid="download-drawing-btn"
                className="group inline-flex items-center gap-2 bg-brand-ink text-white text-[11px] font-semibold tracking-[0.15em] uppercase px-4 py-2.5 hover:bg-brand-red transition-colors duration-300"
              >
                Download Drawing (PDF)
                <Download className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
              </a>
            </div>
          </div>
        </Reveal>
        <div>
          <SectionHeading
            kicker="Standard Section"
            title="Standard MODORS Door Frame Section"
            desc="Two series — PRIME and ECO — in single and double rebate options, manufactured consistently so every frame on your project matches the last."
          />
          <div className="mt-10 border-t border-black/10">
            {SPECS.map((s, i) => (
              <Reveal key={s.label} delay={0.05 * i}>
                <div className="flex items-baseline justify-between gap-6 py-4 border-b border-black/10">
                  <span className="text-xs font-semibold tracking-[0.22em] uppercase text-brand-steel">{s.label}</span>
                  <span className="text-right">
                    <span className="font-display text-xl sm:text-2xl font-semibold uppercase text-brand-ink">{s.value}</span>
                    {s.sub && <span className="block text-xs text-brand-steel mt-0.5 max-w-xs">{s.sub}</span>}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
